import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { format, formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useState } from 'react';
import alertify from 'alertifyjs';

/**
 * InviteDetailsModal - Show detailed invite information
 * 
 * Features:
 * - Display all invite details
 * - Status-based badges
 * - Copy invite link
 * - Expiry countdown
 * - Status-based info messages
 */
const InviteDetailsModal = ({ open, onClose, invite }) => {
  const [copied, setCopied] = useState(false);

  if (!invite) return null;

  // Generate invite link
  const inviteLink = invite.isExistingUser
    ? `${window.location.origin}/invite/accept/${invite.token}`
    : `${window.location.origin}/invite/register/${invite.token}`;

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      alertify.success('Link kopyalandı!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      alertify.error('Link kopyalanamadı');
    }
  };

  // Get status badge color and label
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { color: 'warning', label: 'Beklemede', icon: <HourglassEmptyIcon /> };
      case 'accepted':
        return { color: 'success', label: 'Kabul Edildi', icon: <CheckCircleIcon /> };
      case 'rejected':
        return { color: 'error', label: 'Reddedildi', icon: <CancelIcon /> };
      case 'cancelled':
        return { color: 'default', label: 'İptal Edildi', icon: <CancelIcon /> };
      case 'expired':
        return { color: 'error', label: 'Süresi Doldu', icon: <AccessTimeIcon /> };
      default:
        return { color: 'default', label: status, icon: null };
    }
  };

  // Get user type label
  const getUserTypeLabel = (userType) => {
    return userType === 'company_member' ? 'Şirket Üyesi' : userType;
  };

  // Calculate remaining days
  const getRemainingDays = () => {
    if (invite.status !== 'pending') return null;
    const expiryDate = new Date(invite.expiresAt);
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Get status message
  const getStatusMessage = () => {
    switch (invite.status) {
      case 'pending':
        const remainingDays = getRemainingDays();
        if (remainingDays === 0) {
          return { severity: 'warning', text: 'Bu davet bugün sona erecek!' };
        } else if (remainingDays <= 2) {
          return { severity: 'warning', text: `Bu davet ${remainingDays} gün içinde sona erecek` };
        }
        return { severity: 'info', text: 'Bu davet henüz kabul edilmedi' };
      case 'accepted':
        return { severity: 'success', text: 'Bu davet kabul edildi ve kullanıcı şirkete katıldı' };
      case 'rejected':
        return { severity: 'error', text: 'Bu davet kullanıcı tarafından reddedildi' };
      case 'cancelled':
        return { severity: 'warning', text: 'Bu davet iptal edildi' };
      case 'expired':
        return { severity: 'error', text: 'Bu davetin süresi doldu' };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge(invite.status);
  const statusMessage = getStatusMessage();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">
            Davet Detayları
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Status Message */}
          {statusMessage && (
            <Alert severity={statusMessage.severity} icon={statusBadge.icon}>
              {statusMessage.text}
            </Alert>
          )}

          {/* Email */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                Email
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="medium">
              {invite.email}
            </Typography>
          </Box>

          <Divider />

          {/* User Type & Status */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  Kullanıcı Tipi
                </Typography>
              </Box>
              <Typography variant="body1">
                {getUserTypeLabel(invite.userType)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Durum
              </Typography>
              <Chip
                label={statusBadge.label}
                color={statusBadge.color}
                size="small"
                icon={statusBadge.icon}
              />
            </Box>
          </Box>

          <Divider />

          {/* Invited By */}
          {invite.invitedBy && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Davet Eden
                </Typography>
                <Typography variant="body1">
                  {invite.invitedBy.firstName} {invite.invitedBy.lastName}
                </Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Dates */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  Gönderilme
                </Typography>
              </Box>
              <Typography variant="body2">
                {format(new Date(invite.createdAt), 'dd MMM yyyy', { locale: tr })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(invite.createdAt), { addSuffix: true, locale: tr })}
              </Typography>
            </Box>

            {invite.status === 'pending' && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Son Kullanma
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {format(new Date(invite.expiresAt), 'dd MMM yyyy', { locale: tr })}
                </Typography>
                <Typography variant="caption" color={getRemainingDays() <= 2 ? 'error.main' : 'text.secondary'}>
                  {getRemainingDays()} gün kaldı
                </Typography>
              </Box>
            )}
          </Box>

          {/* Invite Link (only for pending) */}
          {invite.status === 'pending' && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block', mb: 1 }}>
                  Davet Linki
                </Typography>
                <TextField
                  fullWidth
                  value={inviteLink}
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleCopyLink}
                          edge="end"
                          color={copied ? 'success' : 'primary'}
                        >
                          {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      fontFamily: 'monospace'
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InviteDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invite: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired,
    isExistingUser: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    expiresAt: PropTypes.string.isRequired,
    invitedBy: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    })
  })
};

export default InviteDetailsModal;


